import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

/** Subpixel slack when deciding whether two items share a row. */
const ROW_TOLERANCE = 1;

/**
 * Group rects into rows by their `top`, in visual order.
 * @param {DOMRect[]} rects
 * @returns {number[]} The `top` of each distinct row, top to bottom.
 */
function collectRowTops(rects) {
  /** @type {number[]} */
  const tops = [];

  for (const rect of rects) {
    if (!tops.some((top) => Math.abs(top - rect.top) <= ROW_TOLERANCE)) tops.push(rect.top);
  }

  return tops.sort((a, b) => a - b);
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section and other areas.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @attr {string | null} max-rows How many rows of items may stay visible before overflowing. Defaults to 1.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items', 'max-rows'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }

    // `max-rows` can be flipped live by the theme editor, so re-measure with the new limit.
    // Guard on #refs: attributeChangedCallback runs during upgrade, before #initialize().
    if (name === 'max-rows' && oldValue !== newValue && this.#refs) {
      this.#reflowItems();
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    // Styles for dynamically injected <overflow-list> elements are async.
    // We need to wait for them to be loaded before initializing the element to properly calculate the overflow.
    await this.#waitForStyles();

    this.#initialize();
  }

  #waitForStyles() {
    /** @type {HTMLLinkElement | null | undefined} */
    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

    // No styles or styles are already loaded.
    if (!styles || styles.sheet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      styles.addEventListener('load', resolve);
    });
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event */ (event) => {
        this.#reflowItems(0, event.detail.lastVisibleElement);
      }
    );

    // When <overflow-list> is dynamically injected, the browser doesn't remove its <template> automatically.
    // In theory, we could get rid of it now, or in DeclarativeShadowElement, but that would invalidate the layout.
    // Instead, we ignore it for now and remove it later on the first reflow.
    const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    // Observe the first and last elements to trigger a reflow when they are visible.
    // That way we can get their height from the IntersectionObserver for free (without reflows).
    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }
    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  /**
   * How many rows of items are allowed to stay visible before items overflow.
   * Defaults to 1, which is the single-row behaviour every other consumer relies on.
   * @returns {number}
   */
  get maxRows() {
    const value = parseInt(this.getAttribute('max-rows') ?? '', 10);
    return Number.isFinite(value) && value > 1 ? value : 1;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (entry?.isIntersecting) {
      this.#intersectionObserver.disconnect();
      setTimeout(() => {
        // Remove the leftover <template> for dynamically injected <overflow-list> elements.
        this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
        this.#reflowItems(entry.boundingClientRect.height);
      }, 0);
    }
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = () => {
    if (this.#scheduled) return;

    this.#scheduled = true;

    requestAnimationFrame(() =>
      setTimeout(() => {
        this.#reflowItems();
        this.#scheduled = false;
      }, 0)
    );
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
    this.style.removeProperty('--overflow-row-height');
    this.style.removeProperty('--overflow-row-2-offset');
    delete this.dataset.rows;

    for (const element of this.#refs.defaultSlot.assignedElements()) {
      element.removeAttribute('data-upper-row');
    }
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {number} [listHeight] Initial height of the list
   * @param {HTMLElement | null} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#unobserveChanges();

    // Reset all elements to the default slot so we can check which ones overflow.
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    const lastElement = elements[elements.length - 1];

    if (!lastElement) {
      this.#observeChanges();
      return;
    }

    /** @type {Element[]} */
    let visibleElements = [];
    /** @type {Element[]} */
    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    const { maxRows } = this;

    // Pinning the height keeps the list from jumping mid-measurement, but it assumes a single row.
    // When several rows are allowed the list has to be free to grow, so skip it — and drop any
    // height a previous single-row reflow left behind, which is what happens when the merchant
    // flips the layout setting in the theme editor. Otherwise the second row overflows a list
    // still sized to one row and the header never grows to fit it.
    if (maxRows > 1) {
      list.style.removeProperty('height');
      // Cleared before measuring so rows are sized from their natural height. Otherwise the value
      // published by the previous reflow would feed back in and could only ever grow.
      this.style.removeProperty('--overflow-row-height');
    } else if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    }

    // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row.
    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    lastVisibleElement?.style.setProperty('order', '-1');

    const moreSlotRect = moreSlot.getBoundingClientRect();

    // Anything sharing the "More" item's row fits. With `max-rows` above 1 the cutoff moves down
    // to the top of the last row we're willing to keep, so wrapped rows still count as visible.
    let overflowTop = moreSlotRect.top;
    let tolerance = 0;

    if (maxRows > 1) {
      const rowTops = collectRowTops(elements.map((element) => element.getBoundingClientRect()));
      overflowTop = rowTops[Math.min(maxRows, rowTops.length) - 1] ?? overflowTop;
      tolerance = ROW_TOLERANCE;
    }

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > overflowTop + tolerance) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        hasOverflow = true;
        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    });

    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }
    lastVisibleElement?.style.removeProperty('order');

    // Move the elements to the correct slot.
    for (const element of elements) {
      const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    // Adjust the "More" button visibility.
    moreSlot.hidden = !hasOverflow;

    // The measurement above sits "More" at the start of the first row, but it renders at the end of
    // the last one. Across several rows that move can leave it without room and push it onto a row
    // of its own, so trim the visible items until everything — "More" included — fits in `maxRows`.
    // Only reachable when items already overflowed; otherwise "More" is hidden and takes no space.
    if (maxRows > 1 && hasOverflow) {
      for (let guard = elements.length; guard > 0; guard--) {
        const rects = defaultSlot.assignedElements().map((element) => element.getBoundingClientRect());
        if (!moreSlot.hidden) rects.push(moreSlot.getBoundingClientRect());
        if (collectRowTops(rects).length <= maxRows) break;

        const last = visibleElements.pop();
        if (!last) break;

        last.slot = overflowSlot.name;
        overflowingElements.push(last);
      }
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

    // The placeholder reserves the next item's width so a single non-wrapping row can grow back
    // into free space. A wrapping list already grows with its host, and that reserved width would
    // just push items onto an extra row, so it stays out of the way when several rows are allowed.
    if (hasOverflow && maxRows === 1) {
      // Set the width and height of the placeholder so the list can grow if there is space.
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    // Reset the overflow property since children elements may need to display outside the list (e.g. dropdowns, popovers).
    list.style.setProperty('overflow', 'unset');

    // Publish the row geometry for CSS that can only be written once the rows are known.
    // Measured after hiding the "More" slot: freeing its width can pull a wrapped item back up.
    if (maxRows > 1) {
      const slottedElements = defaultSlot.assignedElements();
      const visibleRects = slottedElements.map((element) => element.getBoundingClientRect());
      // "More" shares the rows, so it counts towards both the row tally and the row height.
      if (!moreSlot.hidden) visibleRects.push(moreSlot.getBoundingClientRect());
      const rowTops = collectRowTops(visibleRects);

      this.dataset.rows = `${rowTops.length}`;

      // Mark the items that are NOT on the last row. Anything hanging below them is a matter for
      // the host: an item on an upper row may have empty space underneath it (when the row below
      // is shorter) and whatever it uses to keep hover alive has to reach further down than it
      // does for the last row. Only the element knows which row it landed on, so say so here.
      const lastRowTop = rowTops[rowTops.length - 1];
      for (const [index, element] of slottedElements.entries()) {
        const rect = visibleRects[index];
        const onLastRow = rect == null || lastRowTop == null || Math.abs(rect.top - lastRowTop) <= ROW_TOLERANCE;
        element.toggleAttribute('data-upper-row', !onLastRow);
      }

      // Rows are sized by their tallest item, so a highlighted link (which carries extra padding)
      // makes its own row taller than the others. Publish the tallest item so every row can match it.
      this.style.setProperty('--overflow-row-height', `${Math.max(0, ...visibleRects.map((rect) => rect.height))}px`);

      // Distance from the first row to the second, so a divider can be placed in the actual gap
      // rather than at the list's midpoint — which is not the same thing when rows differ in height.
      const secondRowOffset = rowTops.length > 1 && rowTops[0] != null && rowTops[1] != null ? rowTops[1] - rowTops[0] : 0;
      this.style.setProperty('--overflow-row-2-offset', `${secondRowOffset}px`);
    }

    hasOverflow && this.#updateMinimumReached(visibleElements);

    this.#observeChanges();
  };

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);

  #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
    // Extend the root margin to around one more viewport of a typical mobile screen.
    rootMargin: '640px 360px 640px 360px',
  });
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
